import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  useTheme
} from '@mui/material';
import { GLTransactionEntry } from '../firebase/glTransactionServices';

interface GLTransactionPreviewProps {
  expenseAmount: number;
  expenseCategory: string;
  paymentMethod: string;
}

const GLTransactionPreview: React.FC<GLTransactionPreviewProps> = ({
  expenseAmount,
  expenseCategory,
  paymentMethod
}) => {
  const theme = useTheme();

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Mock the transaction entries based on the current form values
  const mockEntries: GLTransactionEntry[] = [
    {
      accountId: 'expense-account-id',
      accountNumber: '7100000', // This is just a placeholder, would be the actual account number in production
      accountName: expenseCategory || 'Expense Account',
      debit: expenseAmount || 0,
      credit: 0
    },
    {
      accountId: 'bank-account-id',
      accountNumber: '1100000', // This is just a placeholder, would be the actual account number in production
      accountName: paymentMethod || 'Bank Account',
      debit: 0,
      credit: expenseAmount || 0
    }
  ];

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 2
      }}>
        <Typography variant="subtitle1" fontWeight="bold">
          GL Transaction Preview
        </Typography>
        <Chip 
          label="Double-Entry View" 
          color="primary" 
          size="small"
          sx={{ 
            borderRadius: 1,
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.contrastText
          }}
        />
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ 
        borderRadius: 1,
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
      }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Account</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Debit</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Credit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mockEntries.map((entry, index) => (
              <TableRow 
                key={index}
                sx={{ 
                  '&:last-child td, &:last-child th': { border: 0 },
                  backgroundColor: index % 2 === 0 ? 'white' : theme.palette.grey[50]
                }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {entry.accountNumber}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {entry.accountName}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography 
                    variant="body2" 
                    color={entry.debit > 0 ? theme.palette.success.main : undefined}
                    fontWeight={entry.debit > 0 ? 'medium' : undefined}
                  >
                    {entry.debit > 0 ? formatCurrency(entry.debit) : '-'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography 
                    variant="body2" 
                    color={entry.credit > 0 ? theme.palette.error.main : undefined}
                    fontWeight={entry.credit > 0 ? 'medium' : undefined}
                  >
                    {entry.credit > 0 ? formatCurrency(entry.credit) : '-'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
              <TableCell colSpan={2} align="right">
                <Typography variant="subtitle2">Total</Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="subtitle2" color={theme.palette.success.main}>
                  {formatCurrency(mockEntries.reduce((sum, entry) => sum + entry.debit, 0))}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="subtitle2" color={theme.palette.error.main}>
                  {formatCurrency(mockEntries.reduce((sum, entry) => sum + entry.credit, 0))}
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        * This is a preview of how this expense will be recorded in your general ledger.
      </Typography>
    </Box>
  );
};

export default GLTransactionPreview;
